import { observer } from 'mobx-react'
import React, { ReactElement, useState } from 'react'
import { PrimaryButton } from './Button'
import Dropdown, { DropdownItem } from './Dropdown'
import { InputLabel, TextInputWithLabel } from './Input'
import Modal, { ModalProps } from './Modal'
import Radom from '../state/Radom'
import User from '../state/User'
import { errorToast, successToast } from '../util/Util'
import { FIAT_ACCOUNT_FLAGS, FIAT_CURRENCIES_FOR_ACCOUNT_TYPE, FIAT_CURRIENCES_FOR_NETWORK, FIAT_DESTINATION_CURRENCY_IMG, FIAT_PAYMENT_RAIL_IMG, FiatAccountType, FiatDestinationCurrency, FiatDestinationNetwork, RADOM_COLORS } from '../util/Constants'

interface CreateVirtualAccountModalProps extends ModalProps {
  onAccountCreated: () => void
  availableAccountTypes: Array<{ type: FiatAccountType, available: boolean }>
  availableCurrencies: FiatDestinationCurrency[]
}

const CreateVirtualAccountModal = observer(({ ...props }: CreateVirtualAccountModalProps): ReactElement => {
  const [accountName, setAccountName] = useState('')
  const [fiatAccountType, setFiatAccountType] = useState<FiatAccountType>()
  const [destinationNetwork, setDestinationNetwork] = useState<FiatDestinationNetwork>()
  const [destinationCurrency, setDestinationCurrency] = useState<FiatDestinationCurrency>()
  const [currencies, setCurrencies] = useState<FiatDestinationCurrency[]>(props.availableCurrencies)
  const [networks, setNetworks] = useState<FiatDestinationNetwork[]>(
    Object.values(FiatDestinationNetwork).filter(item => {
      const availableCurrencies = props.availableCurrencies
      const allCurrencies = FIAT_CURRIENCES_FOR_NETWORK[item]
      return allCurrencies.every(curr => availableCurrencies.includes(curr))
    }))
  const [isLoading, setIsLoading] = useState(false)

  const createFiatAccount = async (): Promise<void> => {
    if (!fiatAccountType || !destinationNetwork) {
      errorToast('')
      return
    }

    setIsLoading(true)
    try {
      await Radom.createDepositAccount({
        name: accountName,
        accountDetails: {
          Fiat: {
            currency: fiatAccountType,
            network: destinationNetwork,
            token: destinationCurrency
          }
        }
      })
      props.onAccountCreated()
      successToast('Virtual account created')
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsLoading(false)
  }

  return <Modal {...props} innerStyles={{ width: 550 }}>
    <div style={{
      display: 'flex',
      flexGrow: 1,
      flexDirection: 'column',
      justifyContent: 'start',
      gap: 10,
      padding: '0px 30px'
    }}>
      <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', rowGap: 20 }}>
        <TextInputWithLabel
          label='Account nickname'
          placeholder='Enter a nickname for this account'
          disabled={isLoading}
          required
          value={accountName}
          onChange={e => {
            const el = (e.target as HTMLInputElement)
            const value = el.value
            setAccountName(value)
          }}
        />

        <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 10 }}>
          <InputLabel>Account type</InputLabel>
          <Dropdown
            key={fiatAccountType}
            disabled={isLoading}
            selectedContent={
              fiatAccountType
                ? <div style={{
                  fontSize: 14,
                  alignItems: 'center',
                  display: 'flex',
                  gap: 10
                }}>
                  <img
                    style={{
                      width: 20,
                      maxHeight: 20,
                      maxWidth: 20
                    }}
                    src={`https://pay.radom.com/flags/${FIAT_ACCOUNT_FLAGS[fiatAccountType]}.svg`}
                  />
                  {fiatAccountType}
                </div>
                : <span style={{ fontSize: 14 }}>Select bank account type</span>
            }
            dropdownContent={
              props.availableAccountTypes.map(c =>
                <DropdownItem
                  key={c.type}
                  onClick={() => {
                    setFiatAccountType(c.type)
                    setDestinationNetwork(undefined)
                    setDestinationCurrency(undefined)
                    const finalCurrencies = props.availableCurrencies.filter(item =>
                      FIAT_CURRENCIES_FOR_ACCOUNT_TYPE[c.type].includes(item))
                    setCurrencies(finalCurrencies)
                    setNetworks(Object.values(FiatDestinationNetwork).filter(item => {
                      const allCurrencies = FIAT_CURRIENCES_FOR_NETWORK[item]
                      return allCurrencies.every(curr => finalCurrencies.includes(curr))
                    }))
                  }}
                  style={{
                    fontSize: 14,
                    alignItems: 'center',
                    display: 'flex',
                    gap: 10,
                    ...(!c.available
                      ? {
                        cursor: 'not-allowed',
                        pointerEvents: 'none',
                        backgroundColor: RADOM_COLORS.GRAY_LIGHTEST,
                        opacity: 0.5
                      }
                      : {})
                  }}>
                  <img
                    style={{
                      width: 20,
                      maxHeight: 20,
                      maxWidth: 20
                    }}
                    src={`https://pay.radom.com/flags/${FIAT_ACCOUNT_FLAGS[c.type]}.svg`}
                  />
                  {c.type}
                </DropdownItem>
              )
            } />
        </div>

        {
          fiatAccountType && <>
            <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 10 }}>
              <InputLabel>Destination Currency</InputLabel>
              <Dropdown
                key={destinationCurrency}
                disabled={isLoading || currencies.length === 0}
                selectedContent={
                  destinationCurrency
                    ? <div style={{
                      fontSize: 14,
                      alignItems: 'center',
                      display: 'flex',
                      gap: 10
                    }}>
                      <img
                        style={{
                          width: 20,
                          maxHeight: 20,
                          maxWidth: 20
                        }}
                        src={FIAT_DESTINATION_CURRENCY_IMG[destinationCurrency]}
                      />
                      {destinationCurrency}
                    </div>
                    : <span style={{ fontSize: 14 }}>Select destination currency</span>
                }
                dropdownContent={
                  currencies.map(d =>
                    <DropdownItem
                      key={d}
                      onClick={() => {
                        setDestinationCurrency(d)
                        setNetworks(Object.values(FiatDestinationNetwork).filter(item => {
                          const allCurrencies = FIAT_CURRIENCES_FOR_NETWORK[item]
                          return allCurrencies.includes(d)
                        }))
                      }}
                      style={{
                        fontSize: 14,
                        alignItems: 'center',
                        display: 'flex',
                        gap: 10
                      }}>
                      <img
                        style={{
                          width: 20,
                          maxHeight: 20,
                          maxWidth: 20
                        }}
                        src={FIAT_DESTINATION_CURRENCY_IMG[d]}
                      />
                      {d}
                    </DropdownItem>
                  )
                } />
            </div>

            <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 10 }}>
              <InputLabel>Destination network</InputLabel>
              <Dropdown
                key={destinationNetwork}
                disabled={isLoading || networks.length === 0}
                selectedContent={
                  destinationNetwork
                    ? <div style={{
                      fontSize: 14,
                      alignItems: 'center',
                      display: 'flex',
                      gap: 10
                    }}>
                      <img
                        style={{
                          width: 20,
                          maxHeight: 20,
                          maxWidth: 20
                        }}
                        src={FIAT_PAYMENT_RAIL_IMG[destinationNetwork]}
                      />
                      {destinationNetwork}
                    </div>
                    : <span style={{ fontSize: 14 }}>Select destination network</span>
                }
                dropdownContent={
                  networks.map(p =>
                    <DropdownItem
                      key={p}
                      onClick={() => {
                        setDestinationNetwork(p)
                        setCurrencies(FIAT_CURRIENCES_FOR_NETWORK[p].filter(item =>
                          props.availableCurrencies.includes(item)))
                      }}
                      style={{
                        fontSize: 14,
                        alignItems: 'center',
                        display: 'flex',
                        gap: 10
                      }}>
                      <img
                        style={{
                          width: 20,
                          maxHeight: 20,
                          maxWidth: 20
                        }}
                        src={FIAT_PAYMENT_RAIL_IMG[p]}
                      />
                      {p}
                    </DropdownItem>
                  )
                } />
            </div>
          </>
        }
      </div>
    </div>

    {
      User.organization?.verificationStatus === 'Verified' &&
      <div style={{
        padding: 30,
        borderRadius: 15,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        position: 'sticky',
        bottom: 0,
        background: 'linear-gradient(transparent, white 20%)'
      }}>
        <PrimaryButton
          disabled={!accountName || !fiatAccountType || !destinationNetwork }
          isLoading={isLoading}
          style={{ width: '100%' }}
          onClick={() => {
            createFiatAccount()
          }}>
          Create account
        </PrimaryButton>
      </div>
    }
  </Modal>
})

export default CreateVirtualAccountModal
